
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState } from 'react';
import { CONTACT_INFO } from '../config';
import { motion, cubicBezier } from 'framer-motion';
import { MailIcon, PhoneIcon, MapPinIcon, InstagramIcon, PinterestIcon, TikTokIcon, TwitterIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { AppPage, ContactDetails } from '../types';
import { submitContact } from '../../Shared/services/mockBackend';
import { trackEvent } from '../services/analyticsService';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { SOCIAL_LINKS } from '../config';

interface ContactPageProps {
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const ContactPage: React.FC<ContactPageProps> = ({ onNavigate }) => {
    const { t } = useTranslation();

    useSEO(
        t('contactPage_seo_title'),
        t('contactPage_seo_desc'),
        t('contactPage_seo_keywords').split(',').map(k => k.trim())
    );

    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState<ContactDetails>({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            await submitContact(formData);
            trackEvent('contact_submission', {
                subject: formData.subject
            });
            setIsSuccess(true);
            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    const sectionVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1) } },
    };

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
            className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-[#18181B] text-gray-800 dark:text-[#F1F1F1] overflow-y-auto custom-scrollbar"
        >
            <div className="max-w-5xl w-full grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                {/* Contact Info */}
                <div className="bg-[#C0A172] dark:bg-[#C0A172] text-white rounded-3xl p-8 md:p-12 shadow-xl flex flex-col justify-between">
                    <div>
                        <h1 className="text-4xl font-serif font-bold mb-6">{t('contact_title')}</h1>
                        <p className="text-indigo-100 text-lg mb-12">
                            {t('contact_subtitle')}
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/10 p-3 rounded-full">
                                    <MailIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-sm text-indigo-200 uppercase tracking-wider font-bold">{t('contact_email_label')}</p>
                                    <p className="text-lg">{CONTACT_INFO.email.general}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="bg-white/10 p-3 rounded-full">
                                    <PhoneIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-sm text-indigo-200 uppercase tracking-wider font-bold">{t('contact_phone_label')}</p>
                                    {/* <p className="text-lg">{CONTACT_INFO.phone.main}</p> */}
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="bg-white/10 p-3 rounded-full">
                                    <MapPinIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-sm text-indigo-200 uppercase tracking-wider font-bold">{t('contact_office_label')}</p>
                                    <p className="text-lg">{CONTACT_INFO.address.city}, {CONTACT_INFO.address.country}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 pt-8 border-t border-white/10">
                        <p className="text-sm text-indigo-200 mb-4 font-bold uppercase tracking-wider">Follow Us</p>
                        <div className="flex gap-4">
                            <div className="flex gap-3">
                                <a href={SOCIAL_LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors" aria-label="Instagram">
                                    <InstagramIcon className="w-5 h-5" />
                                </a>
                                <a href={SOCIAL_LINKS.PINTEREST} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors" aria-label="Pinterest">
                                    <PinterestIcon className="w-5 h-5" />
                                </a>
                                <a href={SOCIAL_LINKS.TIKTOK} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors" aria-label="TikTok">
                                    <TikTokIcon className="w-5 h-5" />
                                </a>
                                <a href={SOCIAL_LINKS.TWITTER} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors" aria-label="Twitter">
                                    <TwitterIcon className="w-5 h-5" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Form */}
                <div className="bg-white dark:bg-[#2A2A2A] rounded-3xl p-8 md:p-12 shadow-lg border border-gray-100 dark:border-[#C0A172]/10">
                    {isSuccess ? (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-12"
                        >
                            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('contact_success_title')}</h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-8">
                                {t('contact_success_desc')}
                            </p>
                            <button
                                onClick={() => setIsSuccess(false)}
                                className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline"
                            >
                                {t('contact_form_send_another')}
                            </button>
                        </motion.div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded text-red-700 dark:text-red-300 text-sm">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label htmlFor="name" className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{t('contact_form_name')}</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full p-4 bg-gray-50 dark:bg-[#18181B] border border-gray-200 dark:border-[#C0A172]/20 rounded-xl focus:ring-2 focus:ring-[#C0A172] outline-none transition dark:text-[#F1F1F1]"
                                    placeholder={t('contact_form_placeholder_name')}
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{t('contact_form_email')}</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full p-4 bg-gray-50 dark:bg-[#18181B] border border-gray-200 dark:border-[#C0A172]/20 rounded-xl focus:ring-2 focus:ring-[#C0A172] outline-none transition dark:text-[#F1F1F1]"
                                    placeholder={t('contact_form_placeholder_email')}
                                />
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                                    {t('contact_form_phone') || 'Phone'} <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full p-4 bg-gray-50 dark:bg-[#18181B] border border-gray-200 dark:border-[#C0A172]/20 rounded-xl focus:ring-2 focus:ring-[#C0A172] outline-none transition dark:text-[#F1F1F1]"
                                    placeholder={t('contact_form_placeholder_phone') || '+1 234 567 890'}
                                />
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{t('contact_form_subject')}</label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    className="w-full p-4 bg-gray-50 dark:bg-[#18181B] border border-gray-200 dark:border-[#C0A172]/20 rounded-xl focus:ring-2 focus:ring-[#C0A172] outline-none transition dark:text-[#F1F1F1]"
                                    placeholder={t('contact_form_placeholder_subject')}
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{t('contact_form_message')}</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    required
                                    rows={5}
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="w-full p-4 bg-gray-50 dark:bg-[#18181B] border border-gray-200 dark:border-[#C0A172]/20 rounded-xl focus:ring-2 focus:ring-[#C0A172] outline-none transition resize-none dark:text-[#F1F1F1]"
                                    placeholder={t('contact_form_placeholder_message')}
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-[#C0A172] hover:bg-[#A68A5E] text-white font-bold py-4 rounded-xl transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isLoading ? <Spinner className="w-5 h-5" /> : t('contact_form_submit')}
                            </button>
                        </form>
                    )}
                </div>
            </div>
            <Footer onNavigate={onNavigate} />
        </motion.div>
    );
};

export default ContactPage;
